/**
 * Preferenza di tema del sistema operativo, letta dal browser.
 *
 * Serve al solo stato «sistema» del selettore: quando l'utente ha scelto
 * esplicitamente chiaro o scuro, questo valore viene letto ma non usato (vedi
 * `resolveTheme` in `themeMode.ts`).
 *
 * Si usa `useSyncExternalStore` perché la media query è una sorgente esterna a
 * React: si evita lo stato intermedio di un `useEffect` che si sincronizza dopo
 * il primo render, e il valore è corretto fin dal primo paint. È anche ciò che
 * fa seguire il tema **a caldo**, senza ricaricare, a chi cambia la preferenza
 * del sistema mentre la dashboard è aperta.
 */

import { useSyncExternalStore } from 'react';

import { DARK_QUERY, systemPrefersDark } from './themeMode';

/**
 * Registra un ascoltatore sui cambi di preferenza.
 *
 * @param onChange Callback invocata a ogni cambiamento della media query.
 * @returns Funzione che rimuove l'ascoltatore.
 */
function subscribe(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') {
    return () => undefined;
  }
  const query = window.matchMedia(DARK_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

/**
 * Valore usato durante il rendering lato server, dove `window` non esiste.
 *
 * @returns Sempre `false`: il tema chiaro è il default neutro.
 */
function getServerSnapshot(): boolean {
  return false;
}

/**
 * Indica se il sistema preferisce il tema scuro, aggiornandosi in tempo reale.
 *
 * @returns `true` se il tema scuro è preferito, `false` altrimenti.
 */
export function usePrefersDark(): boolean {
  return useSyncExternalStore(subscribe, systemPrefersDark, getServerSnapshot);
}
